import apiAuth from '@src/api/auth';
import authUserStorage from '@src/storage/authUser';

class User {
  constructor(data = {}, isAuthenticated = false) {
    let license = data.license || {};

    this.name = data.name || '';
    this.surname = data.surname || '';
    this.license = {
      serialNumber: license.serialNumber || '',
      customFields: license.customFields || []
    };
    this.isAuthenticated = isAuthenticated;
  }
}

export default {
  state: {
    user: new User(),

    /**
     * Флаг, определяющий запущена или нет синхронизация сеанса пользователя
     * между окнами/вкладками браузера.
     */
    syncWindowsStarted: false
  },

  actions: {
    /**
     * Проверяет наличие сессии пользователя.
     * Возвращает объект Promise, в обработчик которого передается значение true,
     * если сессия существует, или значение false, если сессия отсутствует.
     * При значении параметра mode === 'init' при проверке не будет пытаться восстановить
     * сессию по restoreToken
     *
     * @param {Vuex.Store}
     * @param {string} mode
     * @returns {Promise<boolean, Error>}
     */
    authCheck({ dispatch }, mode = '') {
      return apiAuth.check(mode).then(result => {
        if (result) {
          return dispatch('setUser', { user: result }).then(() => true);
        }
        return dispatch('unsetUser').then(() => false);
      });
    },

    /**
     * Аутентифицирует пользователя по логину и паролю.
     *
     * @param {Vuex.Store}
     * @param {string} login
     * @param {string} password
     * @param {boolean} remember
     * @returns {Promise<void, AxiosError>}
     */
    userLogin({ dispatch }, { login, password, remember }) {
      return apiAuth.login(login, password, remember).then(user => {
        return dispatch('setUser', { user: user });
      });
    },

    /**
     * Завершает сессию пользователя в системе.
     *
     * @param {Vuex.Store}
     * @returns {Promise}
     */
    userLogout({ dispatch }) {
      return apiAuth.logout().then(() => {
        dispatch('unsetUser');
      });
    },

    /**
     * Запускает синхронизацию сеанса пользователя между
     * окнами/вкладками браузера.
     *
     * @param {Vuex.Store}
     * @returns {undefined}
     */
    startSyncWindows({ state, commit, dispatch }) {
      if (state.syncWindowsStarted) {
        return;
      }

      authUserStorage.addUserListener(newUser => {
        if (newUser) {
          dispatch('setUser', {
            user: newUser,
            noSync: true
          });
        } else {
          dispatch('unsetUser', true);
        }
      });

      commit('MUTATION_SET_SYNC_WINDOWS_STARTED', true);
    },

    /**
     * @param {Vuex.Store}
     * @param {Object} payload
     * @returns {Promise<void>}
     */
    setUser({ commit, dispatch }, payload) {
      commit('MUTATION_SET_USER', payload);
      dispatch('setCurrentDeviceSettings')

      return dispatch('loadDatabases', null, { root: true });
    },

    /**
     * @param {Vuex.Store}
     * @param {boolean} [noSync=false]
     * @returns {void}
     */
    unsetUser({ commit }, noSync = false) {
      commit('MUTATION_UNSET_USER', noSync);
      commit('UNSET_DATABASES', null, { root: true });
    }
  },

  mutations: {
    /**
     * @param {Object} state
     * @param {Object} payload
     * @param {Object} payload.user
     * @param {boolean} [payload.noSync=false]
     */
    MUTATION_SET_USER(state, payload) {
      let userData = payload.user || {};

      state.user = new User(userData, true);

      if (state.syncWindowsStarted && !payload.noSync) {
        authUserStorage.setUser(userData);
      }
    },

    /**
     * @param {Object} state
     * @param {boolean} [noSync=false]
     */
    MUTATION_UNSET_USER(state, noSync = false) {
      state.user = new User();

      if (state.syncWindowsStarted && !noSync) {
        authUserStorage.removeUser();
      }
    },

    /**
     * Устанавливает значение опции синхронизации сеанса пользователя
     * между окнами/вкладками браузера.
     *
     * @param {Object} state
     * @param {boolean} value
     */
    MUTATION_SET_SYNC_WINDOWS_STARTED(state, value) {
      state.syncWindowsStarted = value;
    }
  }
};
